import { useState, useEffect } from 'react';
import { supabase } from '../commons/config/settings';
import { toast } from 'react-hot-toast';

export interface Testata {
  id: string;
  base_url: string;
  baseURL?: string; // alias for backward compatibility
  priority: number;
  type: 'DISPLAY' | 'PREROLL';
  urls: string[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateTestataData {
  base_url: string;
  priority: number;
  type: 'DISPLAY' | 'PREROLL';
  urls?: string[];
}

export interface UpdateTestataData {
  base_url?: string;
  priority?: number;
  type?: 'DISPLAY' | 'PREROLL';
  urls?: string[];
}

export const useTestate = () => {
  const [testate, setTestate] = useState<Testata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTestate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('testate')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching testate:', error);
        setError('Errore nel caricamento delle testate');
        return;
      }

      // Normalize data: ensure urls is always an array
      const testateWithAlias = (data || []).map(testata => {
        // Normalize urls field - ensure it's always an array
        let normalizedUrls: string[] = [];
        if (testata.urls) {
          if (Array.isArray(testata.urls)) {
            normalizedUrls = testata.urls;
          } else if (typeof testata.urls === 'string') {
            // Try to parse if it's a JSON string
            try {
              const parsed = JSON.parse(testata.urls);
              normalizedUrls = Array.isArray(parsed) ? parsed : [];
            } catch {
              normalizedUrls = [];
            }
          }
        }
        
        return {
          ...testata,
          urls: normalizedUrls,
          baseURL: testata.base_url // alias for compatibility
        };
      });
      setTestate(testateWithAlias);
    } catch (err) {
      console.error('Failed to fetch testate:', err);
      setError('Errore nel caricamento delle testate');
    } finally {
      setLoading(false);
    }
  };

  const createTestata = async (testataData: CreateTestataData): Promise<{ success: boolean; testata?: Testata; error?: string }> => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('testate')
        .insert([testataData])
        .select()
        .single();

      if (error) {
        console.error('Error creating testata:', error);
        return { success: false, error: 'Errore nella creazione della testata' };
      }

      // Normalize urls field
      const normalizedUrls = Array.isArray(data.urls) ? data.urls : [];
      const testataWithAlias = { 
        ...data, 
        urls: normalizedUrls,
        baseURL: data.base_url 
      };
      setTestate(prev => [testataWithAlias, ...prev]);
      toast.success('Testata creata con successo!');
      return { success: true, testata: testataWithAlias };
    } catch (err) {
      console.error('Failed to create testata:', err);
      return { success: false, error: 'Errore nella creazione della testata' };
    } finally {
      setLoading(false);
    }
  };

  const updateTestata = async (id: string, updates: UpdateTestataData): Promise<{ success: boolean; testata?: Testata; error?: string }> => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('testate')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating testata:', error);
        return { success: false, error: 'Errore nell\'aggiornamento della testata' };
      }

      // Normalize urls field
      const normalizedUrls = Array.isArray(data.urls) ? data.urls : [];
      const testataWithAlias = { 
        ...data, 
        urls: normalizedUrls,
        baseURL: data.base_url 
      };
      setTestate(prev => prev.map(testata => testata.id === id ? testataWithAlias : testata));
      toast.success('Testata aggiornata con successo!');
      return { success: true, testata: testataWithAlias };
    } catch (err) {
      console.error('Failed to update testata:', err);
      return { success: false, error: 'Errore nell\'aggiornamento della testata' };
    } finally {
      setLoading(false);
    }
  };

  const deleteTestata = async (id: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('testate')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting testata:', error);
        return { success: false, error: 'Errore nell\'eliminazione della testata' };
      }

      setTestate(prev => prev.filter(testata => testata.id !== id));
      toast.success('Testata eliminata con successo!');
      return { success: true };
    } catch (err) {
      console.error('Failed to delete testata:', err);
      return { success: false, error: 'Errore nell\'eliminazione della testata' };
    } finally {
      setLoading(false);
    }
  };

  const updateTestataUrls = async (id: string, urls: string[]): Promise<{ success: boolean; error?: string }> => {
    return updateTestata(id, { urls });
  };

  // Optimistic local update without DB call
  const optimisticUpdateTestata = (id: string, updates: Partial<Testata>) => {
    setTestate(prev => prev.map(testata => 
      testata.id === id ? { ...testata, ...updates } : testata
    ));
  };

  useEffect(() => {
    fetchTestate();
  }, []);

  return {
    testate,
    loading,
    error,
    fetchTestate,
    createTestata,
    updateTestata,
    deleteTestata,
    updateTestataUrls,
    optimisticUpdateTestata,
    clearError: () => setError(null)
  };
}; 